import type { MatchedField } from "../../../components/team/DuplicateEmployees/duplicatePairs";
import { formatPersonFullName } from "../../../utils/formatters";
import type { NurseMergeResult, SuspectRecord } from "../../../utils/nurseMergeModels";
import type { NurseResult, RosterMatchedOn } from "../hooks";

/** The shape `MergeDuplicateDialog` takes for each side of a pair. */
export type MergeDialogRecord = { id: number; firstName: string; lastName: string };

export type MergePair = {
	/** The record the merge starts from — the one kept unless the person picks the other on Compare. */
	nurse: MergeDialogRecord;
	pairedNurse: MergeDialogRecord;
};

/** One pair of records an upload row leaves to merge, before it is laid out for the grid. */
export type ReviewRecords = {
	recordHere: SuspectRecord;
	existing: SuspectRecord;
	matchedOn: RosterMatchedOn[];
	/** Which record the merge keeps by default: the one that already holds the person's contact. */
	keep: "here" | "existing";
};

/** One review row: an upload row and one of the pairs it leaves to merge. */
export type FloatPoolMergeRow = ReviewRecords & {
	/** `<result index>-<pair index>` — rows carry no id of their own, and one record can appear on several rows. */
	key: string;
	/** The result row this was built from. */
	source: NurseResult;
	uploadedName: string;
	/** The same values, as the record cell washes them on both sides. */
	matched: ReadonlySet<MatchedField>;
	/** False when the row failed — two same-name records here and nothing in the row to tell them apart. */
	applied: boolean;
	pair: MergePair;
};

/** A result row the loader read a name from — every row that reports another record is one. */
type NamedResult = NurseResult & { first_name: string; last_name: string };

const NAME_ONLY: RosterMatchedOn[] = ["name"];

const MATCHED_FIELD: Record<RosterMatchedOn, MatchedField> = { email: "email", phone_number: "phone", name: "name" };

const toDialogRecord = (record: SuspectRecord): MergeDialogRecord => ({ id: record.nurseId, firstName: record.firstName, lastName: record.lastName });

// Rows without a name failed before matching, so they never carry another record.
const hasName = (row: NurseResult): row is NamedResult => row.first_name !== undefined && row.last_name !== undefined;

/** The same-name records a failed row could not choose between, when there are at least two to merge. */
export const unresolvedSameName = (row: NurseResult): SuspectRecord[] | null =>
	row.same_name_records !== undefined && row.same_name_records.length >= 2 ? row.same_name_records : null;

/**
 * Every pair of records a row leaves to merge — none for a linked row, whose facility was already added to the
 * person's existing record. The default record to keep comes from why the other record matched:
 * - `contact`: the other record holds the row's email/phone — keep it.
 * - `same_name`: the row's contact picked the record here — keep the record here.
 * - Two same-name records and nothing to tell them apart: the first reported record, paired with each other one.
 */
export const reviewRecordsFor = (row: NurseResult): ReviewRecords[] => {
	const sameName = unresolvedSameName(row);
	if (sameName) {
		const [first, ...others] = sameName;
		return others.map((other) => ({ recordHere: first, existing: other, matchedOn: NAME_ONLY, keep: "here" }));
	}
	const here = row.record_here;
	if (!here || !row.float_pool_matches) return [];
	return row.float_pool_matches
		.filter((match) => match.kind !== "linked")
		.map((match) => ({ recordHere: here, existing: match, matchedOn: match.matchedOn, keep: match.kind === "contact" ? "existing" : "here" }));
};

export const mergePairFor = ({ recordHere, existing, keep }: ReviewRecords): MergePair =>
	keep === "existing"
		? { nurse: toDialogRecord(existing), pairedNurse: toDialogRecord(recordHere) }
		: { nurse: toDialogRecord(recordHere), pairedNurse: toDialogRecord(existing) };

const toMergeRows = (row: NamedResult, index: number, lastNameFirst: boolean): FloatPoolMergeRow[] =>
	reviewRecordsFor(row).map((records, pairIndex) => ({
		...records,
		key: `${index}-${pairIndex}`,
		source: row,
		uploadedName: formatPersonFullName({ firstName: row.first_name, lastName: row.last_name }, lastNameFirst),
		matched: new Set(records.matchedOn.map((field) => MATCHED_FIELD[field])),
		applied: row.action !== "failed",
		pair: mergePairFor(records),
	}));

/** Every pair the upload leaves to merge, in upload order. */
export const floatPoolMergeRows = (results: NurseResult[], lastNameFirst: boolean): FloatPoolMergeRow[] =>
	results.flatMap((row, index) => (hasName(row) ? toMergeRows(row, index, lastNameFirst) : []));

/**
 * What a merge did to a review row. `merged`: the row's two records were the pair just merged. `superseded`:
 * one of its records was merged away into a record outside this row, so the pair it offers no longer exists.
 */
export type RowMergeOutcome = { kind: "merged" } | { kind: "superseded"; removedId: number; keptId: number };

/**
 * The rows a merge changed, by key. A row pairing the surviving record with a third one is left out — that
 * merge is still real and still pending.
 */
export const mergeOutcomes = (rows: FloatPoolMergeRow[], result: NurseMergeResult): Map<string, RowMergeOutcome> => {
	const mergedPair = new Set([result.primaryNurseId, result.mergedNurseId]);
	const outcomes = new Map<string, RowMergeOutcome>();
	for (const row of rows) {
		const ids = [row.pair.nurse.id, row.pair.pairedNurse.id];
		if (ids.every((id) => mergedPair.has(id))) outcomes.set(row.key, { kind: "merged" });
		else if (ids.includes(result.mergedNurseId)) outcomes.set(row.key, { kind: "superseded", removedId: result.mergedNurseId, keptId: result.primaryNurseId });
	}
	return outcomes;
};
